import { Badge, Box, Button, Tooltip, Typography } from '@mui/material'
import TuneIcon from '@mui/icons-material/Tune'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import FilterDatePreset from './FilterDatePreset'
import FilterBranchSelect from './FilterBranchSelect'
import FilterDrawer from './FilterDrawer'
import FilterChips, { LABEL_KEY } from './FilterChips'
import type { ExecutiveState } from 'app/slice/executive.slice'
import type { ExecutiveTab } from 'api/executive/executive.types'
import { colors } from 'constants/theme'
import { activeIneffectiveFilterKeys, countActiveAdvancedFilters, hasAdvancedFilters } from './filter-config'

interface Props {
  tab: ExecutiveTab
  userScope: 'own-branch' | 'all-branches'
  userBranchIds?: number[]
}

export default function ExecutiveFilterBar({ tab, userScope, userBranchIds }: Props) {
  const { t } = useTranslation()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const filters = useSelector((s: { executive: ExecutiveState }) => s.executive.filters)

  // Count every active drawer filter — not just the ones this tab's drawer
  // edits — so the badge never reads 0 while a chip from another tab is
  // still narrowing the data.
  const activeCount = countActiveAdvancedFilters(filters)
  const advancedTooltip = activeCount > 0
    ? `${t('EXECUTIVE.FILTER.ADVANCED_TOOLTIP')}\n\n${t('EXECUTIVE.FILTER.ACTIVE_COUNT', { count: activeCount })}`
    : t('EXECUTIVE.FILTER.ADVANCED_TOOLTIP')
  // Filters that are active but this tab's widgets ignore (e.g. doctor on the
  // account tab) — say so in words next to the muted chips (UR-2609-1158).
  const ignoredLabels = activeIneffectiveFilterKeys(tab, filters)
    .map((k) => t(`EXECUTIVE.FILTER.${LABEL_KEY[k]}`))

  return (
    <Box sx={{ py: 2, borderBottom: 1, borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <FilterDatePreset />
        <FilterBranchSelect userScope={userScope} userBranchIds={userBranchIds} />
        {hasAdvancedFilters(tab) && (
          <Tooltip
            title={advancedTooltip}
            arrow
            placement="bottom"
            componentsProps={{ tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 360 } } }}
          >
            <Badge
              badgeContent={activeCount}
              invisible={activeCount === 0}
              overlap="rectangular"
              sx={{ '.MuiBadge-badge': { backgroundColor: colors.themeSecondColor, color: colors.white } }}
            >
              <Button
                variant={activeCount > 0 ? 'contained' : 'outlined'}
                size="small"
                startIcon={<TuneIcon />}
                onClick={() => setDrawerOpen(true)}
                // Pin height to 2rem so it lines up with the date/branch
                // Selects, and force the teal palette so the button stops
                // bleeding MUI's default primary blue (the app has no
                // createTheme override).
                sx={{
                  height: '2rem',
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  ...(activeCount > 0
                    ? {
                        backgroundColor: colors.themeSecondColor,
                        color: colors.white,
                        borderColor: colors.themeSecondColor,
                        '&:hover': {
                          backgroundColor: colors.themeSecondDarkColor,
                          borderColor: colors.themeSecondDarkColor,
                        },
                      }
                    : {
                        color: colors.themeSecondColor,
                        borderColor: colors.borderInput,
                        '&:hover': {
                          backgroundColor: colors.themeSecondColor06,
                          borderColor: colors.themeSecondColor,
                        },
                      }),
                }}
              >
                {t('EXECUTIVE.FILTER.ADVANCED_BUTTON')}
              </Button>
            </Badge>
          </Tooltip>
        )}
      </Box>
      <FilterChips tab={tab} />
      {ignoredLabels.length > 0 && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: colors.textLightGray }}>
          {t('EXECUTIVE.FILTER.INEFFECTIVE_NOTICE', { filters: ignoredLabels.join(' / ') })}
        </Typography>
      )}
      <FilterDrawer tab={tab} open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </Box>
  )
}
